import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

/**
 * Guided M3 Consent Request form. Builds the v3-shaped consent envelope
 * the connector's {@code POST /api/abdm/api/v3/frontend/consent/request}
 * accepts. Keeps the patient + HIU + practitioner identifiers as
 * tenant-default-driven so a small clinic doesn't have to invent values.
 */

interface ConsentContext {
  tenantId: string;
  abhaAddress: string;
  hiuId: string;
  requesterName: string;
  requesterRegNo: string;
}

const ALL_HI_TYPES = [
  'OPConsultation',
  'DischargeSummary',
  'ImmunizationRecord',
  'WellnessRecord',
  'DiagnosticReport',
  'Prescription',
  'HealthDocumentRecord',
  'Invoice',
];

const PURPOSES: { code: string; text: string }[] = [
  { code: 'CAREMGT',  text: 'Care Management' },
  { code: 'BTG',      text: 'Break the Glass' },
  { code: 'PUBHLTH',  text: 'Public Health' },
  { code: 'HPAYMT',   text: 'Healthcare Payment' },
  { code: 'DSRCH',    text: 'Disease-specific Research' },
  { code: 'PATRQT',   text: 'Self-requested' },
];

export function M3ConsentForm({
  context,
  onSubmit,
  submitting,
}: {
  context: ConsentContext;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  submitting: boolean;
}) {
  const now = new Date();
  const inThirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

  const [abhaAddress, setAbhaAddress] = useState(context.abhaAddress);
  const [hiTypes, setHiTypes] = useState<Set<string>>(new Set(['OPConsultation', 'Prescription']));
  const [purpose, setPurpose] = useState(PURPOSES[0]?.code ?? 'CAREMGT');
  const [fromDate, setFromDate] = useState(toLocalInput(oneYearAgo));
  const [toDate, setToDate] = useState(toLocalInput(now));
  const [expiry, setExpiry] = useState(toLocalInput(inThirtyDays));

  const toggle = (t: string) => {
    const next = new Set(hiTypes);
    if (next.has(t)) next.delete(t); else next.add(t);
    setHiTypes(next);
  };
  const addAll = () => setHiTypes(new Set(ALL_HI_TYPES));
  const removeAll = () => setHiTypes(new Set());

  const submit = () => onSubmit({
    tenantId: context.tenantId,
    consent: {
      purpose: {
        code: purpose,
        text: PURPOSES.find((p) => p.code === purpose)?.text ?? purpose,
        refUri: 'https://www.abdm.gov.in/purpose/' + purpose,
      },
      patient: { id: abhaAddress },
      hip: null,
      hiu: { id: context.hiuId },
      requester: {
        name: context.requesterName,
        identifier: { type: 'REGNO1', value: context.requesterRegNo, system: 'https://www.mciindia.org' },
      },
      hiTypes: Array.from(hiTypes),
      permission: {
        accessMode: 'VIEW',
        dateRange: { from: toIsoUTC(fromDate), to: toIsoUTC(toDate) },
        dataEraseAt: toIsoUTC(expiry),
        frequency: { unit: 'HOUR', value: 1, repeats: 0 },
      },
    },
  });

  return (
    <Stack spacing={2}>
      <Typography variant="overline" color="text.tertiary">TENANT CONTEXT</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small" label="Tenant ID" value={context.tenantId}
          InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Chip label="Auto" size="small" color="success" sx={{ height: 18, fontSize: 10 }} /></InputAdornment> }}
          helperText="From your login"
          sx={{ flex: 1 }}
        />
        <TextField
          size="small" label="HIU ID" value={context.hiuId}
          InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Chip label="Auto" size="small" color="success" sx={{ height: 18, fontSize: 10 }} /></InputAdornment> }}
          helperText="From tenant ABDM config"
          sx={{ flex: 1 }}
        />
      </Stack>

      <Typography variant="overline" color="text.tertiary">PATIENT DETAILS</Typography>
      <TextField size="small" label="Patient's ABHA Address *" value={abhaAddress} onChange={(e) => setAbhaAddress(e.target.value)} placeholder="arjun_2002@sbx" helperText="The patient's PHR address" />

      <Typography variant="overline" color="text.tertiary">HEALTH INFO DURATION</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField size="small" type="datetime-local" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ flex: 1 }} helperText="DD/MM/YYYY HH:MM — records from" />
        <TextField size="small" type="datetime-local" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ flex: 1 }} helperText="DD/MM/YYYY HH:MM — records up to" />
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField size="small" select label="Purpose *" value={purpose} onChange={(e) => setPurpose(e.target.value)} sx={{ flex: 1 }}>
          {PURPOSES.map((p) => <MenuItem key={p.code} value={p.code}>{p.text}</MenuItem>)}
        </TextField>
        <TextField size="small" type="datetime-local" label="Consent Expiry *" InputLabelProps={{ shrink: true }} value={expiry} onChange={(e) => setExpiry(e.target.value)} sx={{ flex: 1 }} helperText="DD/MM/YYYY HH:MM — when consent stops" />
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline" color="text.tertiary">HEALTH INFO TYPE *</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={addAll} variant="outlined">Add All</Button>
          <Button size="small" onClick={removeAll}>Remove All</Button>
        </Stack>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 0.5 }}>
        {ALL_HI_TYPES.map((t) => (
          <FormControlLabel
            key={t}
            control={<Checkbox size="small" checked={hiTypes.has(t)} onChange={() => toggle(t)} />}
            label={<Typography variant="body2">{t}</Typography>}
          />
        ))}
      </Box>

      <Stack direction="row" justifyContent="flex-end">
        <Button variant="contained" disabled={submitting || hiTypes.size === 0 || !abhaAddress} onClick={submit}>
          {submitting ? 'Submitting…' : 'Submit Consent Request'}
        </Button>
      </Stack>
    </Stack>
  );
}

// ─── helpers ──────────────────────────────────────────────────────

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toIsoUTC(local: string): string {
  if (!local) return '';
  const d = new Date(local);
  return d.toISOString();
}
